import { FormikHelpers } from 'formik';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { mapPayloadToUpsertInput } from 'modules/organizer/domain/data-mappers/organizer-upsert.data-mapper';
import { OrganizerUpsertPayload } from 'modules/organizer/domain/interfaces/organizer-upsert-payload';
import { upsertOrganizer } from 'modules/organizer/store/organizer/actions';
import { OrganizerFormValues } from 'modules/organizer/ui/components/organizer-form';
import { useRefetchOrganizer } from 'modules/organizer/ui/hooks/use-refetch-organizer';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import { toast } from 'react-toastify';
import { Background, backgroundColors } from 'shared/constants/backgrounds';
import { useFetchLogoBackground } from 'shared/hooks/fetchering/use-fetch-logo-background';
import { useUpdateLogoBackground } from 'shared/hooks/mutations/use-update-logo-background';
import { deriveAsyncState } from 'shared/utils/async-state';
import { useAppDispatch } from 'store/hooks';

export function useProfileForm() {
  const dispatch = useAppDispatch();
  const { t } = useLingui();
  const { isLoading: loading, info: loadedInfo, isSaving, error, loadedTournamentId } = useOrganizer();
  const { data: logoBackground, isFetching: isLogoBackgroundFetching } = useFetchLogoBackground();
  const { user, hasTournaments, userTournamentId } = useAuth();
  const { updateLogoBackground } = useUpdateLogoBackground();
  const refetch = useRefetchOrganizer();

  /**
   * Правим только активную организацию. Данные другой в сторе — не «почти то же самое»:
   * форма ушла бы в `upsertTournament` с её `id` и перезаписала бы её данные. Слайс такие
   * данные и так чистит (`fetchOrganizer.pending` / `rejected`), но вкладка им не доверяет:
   * цена ошибки — порча чужой организации.
   */
  const info = loadedInfo && loadedInfo.id === userTournamentId ? loadedInfo : null;

  const initialValues = useMemo((): (OrganizerUpsertPayload & { backgroundColor?: Background }) | undefined => {
    if (!info) return undefined;
    return {
      id: info.id,
      name: info.name,
      sportType: info.sportType,
      email: info.email,
      phone: info.phone,
      description: info.description,
      logoUrl: info.logoUrl,
      address: info.address,
      backgroundColor: backgroundColors[logoBackground?.type ?? 0],
    };
  }, [info, logoBackground]);

  const isLoading = loading || isLogoBackgroundFetching;

  /**
   * Вкладка только правит организацию, поэтому «нечего показать» здесь — не одно
   * состояние, а два разных: организации у пользователя нет вовсе (`empty`, ведём на
   * страницу создания) либо она есть, но не загрузилась (`error`, даём «Повторить»).
   * Ответ `null` при `hasTournaments` — тоже сбой загрузки: правки без `id` не бывает.
   */
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: info ? 1 : 0,
        hasCurrentData: Boolean(info) || !hasTournaments,
        isFetching: isLoading,
        isPending: hasTournaments && loadedTournamentId === null,
        isError: hasTournaments && !info && (Boolean(error) || loadedTournamentId !== null),
      }),
    [info, hasTournaments, isLoading, loadedTournamentId, error],
  );

  async function handleSubmit(data: OrganizerUpsertPayload, formikHelpers: FormikHelpers<OrganizerFormValues>) {
    // Без `id` бэкенд трактует `upsertTournament` как создание — именно так вкладка
    // «Профиль» однажды завела пользователю вторую организацию.
    if (!info?.id) {
      toast.error(t`Данные организации не загружены — сохранить изменения нельзя`);
      formikHelpers.setSubmitting(false);
      return;
    }

    const params = mapPayloadToUpsertInput({ ...data, id: info.id }, user!.id);
    const bgColor = data.logo?.[0]?.background;

    try {
      const [organizerResult] = await Promise.allSettled([
        dispatch(upsertOrganizer(params)).unwrap(),
        updateLogoBackground({ bgColor, tournamentId: info.id }),
      ]);

      if (organizerResult.status === 'fulfilled') {
        toast.success(t`Данные сохранены`);
      } else {
        toast.error(t`Что-то пошло не так. Проверьте данные, возможно, организация с таким названием уже существует`);
      }
    } finally {
      formikHelpers.setSubmitting(false);
    }
  }

  return {
    initialValues,
    isLoading,
    isSaving,
    state,
    error,
    refetch,
    handleSubmit,
  };
}
